import React, { useState, useEffect, useMemo } from "react";
import api from "../../api";

const TMTrainerModal = ({
  isOpen,
  toggle,
  spawnText,
  playerPos
}) => {
  // -- common state
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("exchange");

  // -- TM‐exchange state
  const [options, setOptions] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // -- Sell‐shards state
  const SHARD_IDS = [51, 53, 52, 54, 47, 49, 50, 46, 124, 123,83,118,119]; // red, blue, green, dark, white, purple, ghost, feather, dragon fang
  const [sellQuantity, setSellQuantity] = useState({}); // { [item_id]: qty }

  // mapping of our custom shard images & names with prices
  const SHARD_INFO = {
    51: { name: "Red Shard",   image: "items/Red Shard.png", price: 25 },
    53: { name: "Blue Shard",  image: "items/Blue Shard.png", price: 25 },
    52: { name: "Green Shard", image: "items/Green Shard.png", price: 25 },
    47: { name: "Dark Shard",  image: "items/Dark Shard.png", price: 25 },
    49: { name: "White Shard", image: "items/White Shard.png", price: 20 },
    50: { name: "Purple Shard", image: "items/Purple Shard.png", price: 35 },
    46: { name: "Ghost Shard", image: "items/Ghost Shard.png", price: 25 },
    123: { name: "Dragon Fang", image: "items/dragonfang.png", price: 45 },
    124: { name: "Feather", image: "items/Feather.png", price: 25 },
    83: { name: "Lucky Punch", image: "items/lucky-punch.png", price: 30 },
    54: { name: "Yellow Shard", image: "items/yellowshard.png", price: 25 },
    118: { name: "Ice", image: "items/Ice.png", price: 25 },
    119: { name: "Buginium", image: "items/Buginium.png", price: 25 },
  };

  // fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      (async () => {
        setLoading(true);
        try {
          const [optsRes, invRes] = await Promise.all([
            api.get("/v1/tm/exchange/options", { withCredentials: true }),
            api.get("/v1/inventory", { withCredentials: true }),
          ]);
          setOptions(optsRes.data);
          setInventory(invRes.data);
        } catch (err) {
          console.error("Failed to load TM data", err);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [isOpen]);

  // --- TM Exchange helpers
  const moveTypes = useMemo(
    () => Array.from(new Set(options.map((o) => o.type))).sort(),
    [options]
  );
  const visibleTMs = useMemo(
    () =>
      options.filter((o) => {
        if (filterType && o.type !== filterType) return false;
        const term = searchTerm.toLowerCase();
        return (
          !term ||
          o.move_name.toLowerCase().includes(term) ||
          o.tm_item_name.toLowerCase().includes(term)
        );
      }),
    [options, filterType, searchTerm]
  );

  const handleExchange = async (tm) => {
    try {
      await api.post(
        "/v1/tm/exchange",
        { tm_id: tm.tm_item_id },
        { withCredentials: true }
      );
      
      // Show success message with floating text and close modal
      if (spawnText && playerPos) {
        spawnText(
          `+1 ${tm.tm_item_name}!`,
          playerPos.x,
          playerPos.y - 20,
          '#4ade80',
          tm.tm_item_image
        );
      }
      
      // Close modal immediately after successful purchase
      toggle();
      
    } catch (err) {
      if (spawnText && playerPos) {
        spawnText(
          err.response?.data?.detail || "Exchange failed.",
          playerPos.x,
          playerPos.y - 20,
          '#f87171'
        );
      }
    }
  };

  // --- Sell shards helper
  const handleSell = async (item_id) => {
    const qty = Number(sellQuantity[item_id] || 1);
    const shardInfo = SHARD_INFO[item_id];
    
    try {
      await api.post(
        "/v1/shards/sell",
        { item_id, quantity: qty },
        { withCredentials: true }
      );
      
      // Show success message with floating text and close modal
      if (spawnText && playerPos) {
        const totalCoins = (shardInfo?.price || 0) * qty;
        spawnText(
          `+${totalCoins} coins!`,
          playerPos.x,
          playerPos.y - 20,
          '#4ade80',
          '/items/coin.png'
        );
      }
      
      // Close modal immediately after successful sale
      toggle();
      
    } catch (err) {
      if (spawnText && playerPos) {
        spawnText(
          err.response?.data?.detail || "Sell failed.",
          playerPos.x,
          playerPos.y - 20,
          '#f87171'
        );
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="game-popup-overlay"
      onClick={toggle}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 10px',
        boxSizing: 'border-box',
        zIndex: 1050
      }}
    >
      <div 
        className="tm-trainer-popup"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '95%',
          maxWidth: 1200,
          maxHeight: '95vh',
          background: 'rgba(29, 34, 46, 0.95)',
          color: '#fff',
          borderRadius: 12,
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          margin: '10px'
        }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>TM Trainer Shop</h3>
          </div>
          <button
            onClick={toggle}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              opacity: 0.7
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.7'}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{
          padding: '20px',
          overflowY: 'auto',
          flex: 1
        }}>
          {/* Tab Navigation */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setActiveTab('exchange')}
              style={{
                background: activeTab === 'exchange' ? 'rgba(255, 193, 7, 0.8)' : 'rgba(108, 117, 125, 0.6)',
                border: activeTab === 'exchange' ? '1px solid rgba(255, 193, 7, 0.8)' : '1px solid rgba(108, 117, 125, 0.4)',
                color: '#fff',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: activeTab === 'exchange' ? 'bold' : 'normal',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'exchange') {
                  e.target.style.background = 'rgba(108, 117, 125, 0.8)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'exchange') {
                  e.target.style.background = 'rgba(108, 117, 125, 0.6)';
                }
              }}
            >
              🔄 TM Exchange
            </button>
            <button
              onClick={() => setActiveTab('sell')}
              style={{
                background: activeTab === 'sell' ? 'rgba(255, 193, 7, 0.8)' : 'rgba(108, 117, 125, 0.6)',
                border: activeTab === 'sell' ? '1px solid rgba(255, 193, 7, 0.8)' : '1px solid rgba(108, 117, 125, 0.4)',
                color: '#fff',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: activeTab === 'sell' ? 'bold' : 'normal',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'sell') {
                  e.target.style.background = 'rgba(108, 117, 125, 0.8)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'sell') {
                  e.target.style.background = 'rgba(108, 117, 125, 0.6)';
                }
              }}
            >
              💎 Sell Shards
            </button>
          </div>


          {loading ? (
            <div style={{ 
              textAlign: 'center', 
              color: '#fff', 
              padding: '40px',
              fontSize: '18px'
            }}>
              Loading TM data...
            </div>
          ) : activeTab === 'exchange' ? (
            // TM Exchange Tab
            <div>
              {/* Filters */}
              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                marginBottom: '20px',
                flexWrap: 'wrap'
              }}>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    color: '#fff',
                    padding: '8px 12px',
                    minWidth: '150px'
                  }}
                >
                  <option value="">All Types</option>
                  {moveTypes.map((t) => (
                    <option key={t} value={t} style={{ background: '#2d3748' }}>{t}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Search TMs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    color: '#fff',
                    padding: '8px 12px',
                    flex: 1,
                    minWidth: '200px'
                  }}
                />
              </div>

              {/* TM Grid */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                gap: '16px' 
              }}>
                {visibleTMs.map((tm) => {
                  const costs = tm.exchange_costs.map((cost) => {
                    const inv = inventory.find((i) => i.item === cost.item_id);
                    return { ...cost, youHave: inv?.amount || 0 };
                  });
                  const canExchange = costs.every((c) => c.youHave >= c.amount);

                  return (
                    <div key={tm.tm_item_id} style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '12px',
                      padding: '16px'
                    }}>
                      <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                        <img
                          src={`/${tm.tm_item_image}`}
                          alt={tm.tm_item_name}
                          style={{ width: 48, height: 48, marginBottom: '8px' }}
                        />
                        <h6 style={{ color: '#fff', margin: '0 0 4px', fontWeight: 'bold' }}>
                          {tm.tm_item_name}
                        </h6>
                        <div style={{
                          background: 'rgba(0, 0, 0, 0.2)',
                          borderRadius: '6px',
                          padding: '8px',
                          marginBottom: '12px'
                        }}>
                          <div style={{ color: '#ffc107', fontWeight: 'bold', marginBottom: '4px' }}>
                            {tm.move_name} ({tm.type})
                          </div>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            fontSize: '12px', 
                            color: '#ccc' 
                          }}>
                            <span>Power: {tm.attack || "-"}</span>
                            <span>Acc: {tm.accuracy}%</span>
                            <span>PP: {tm.pp}</span>
                          </div>
                        </div>
                      </div>

                      {/* Costs */}
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ color: '#fff', fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
                          Exchange Cost:
                        </div>
                        {costs.map((c) => (
                          <div key={c.item_id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '12px',
                            marginBottom: '4px',
                            background: 'rgba(0, 0, 0, 0.1)',
                            borderRadius: '4px',
                            padding: '6px 8px'
                          }}>
                            <img
                              src={`/${c.image}`}
                              alt={c.name}
                              style={{ width: 20, height: 20 }}
                            />
                            <span style={{ flex: 1, color: '#fff' }}>
                              {c.amount}× {c.name}
                            </span>
                            <span style={{ 
                              color: c.youHave >= c.amount ? '#4ade80' : '#f87171',
                              fontWeight: 'bold'
                            }}>
                              {c.youHave}/{c.amount}
                            </span>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={() => handleExchange(tm)}
                        disabled={!canExchange}
                        style={{
                          background: canExchange ? 'rgba(74, 144, 226, 0.8)' : 'rgba(108, 117, 125, 0.5)',
                          border: 'none',
                          color: '#fff',
                          padding: '10px',
                          borderRadius: '6px',
                          cursor: canExchange ? 'pointer' : 'not-allowed',
                          width: '100%',
                          fontWeight: 'bold',
                          opacity: canExchange ? 1 : 0.6
                        }}
                      >
                        Exchange
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            // Sell Shards Tab
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
              gap: '16px' 
            }}>
              {SHARD_IDS.map((id) => {
                const inv = inventory.find((i) => i.item === id);
                const youHave = inv?.amount || 0;
                const { name, image, price } = SHARD_INFO[id] || {};
                const qty = Number(sellQuantity[id] || 1);

                return (
                  <div key={id} style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    padding: '16px',
                    textAlign: 'center'
                  }}>
                    <img
                      src={`/${image}`}
                      alt={name}
                      style={{ width: 48, height: 48, marginBottom: '8px' }}
                    />
                    <h6 style={{ color: '#fff', fontWeight: 'bold', marginBottom: '8px' }}>
                      {name}
                    </h6>
                    <p style={{ color: '#ccc', margin: '0 0 12px', fontSize: '14px' }}>
                      You have: <strong style={{ color: '#fff' }}>{youHave}</strong>
                    </p>
                    
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '6px',
                      padding: '8px',
                      marginBottom: '12px'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        gap: '4px',
                        color: '#ffc107',
                        fontWeight: 'bold',
                        marginBottom: '8px'
                      }}>
                        <img src="/items/coin.png" alt="coins" style={{ width: 16, height: 16 }} />
                        {price} each
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <label style={{ color: '#ccc', fontSize: '12px' }}>Qty:</label>
                        <input
                          type="number"
                          min="1"
                          max={youHave}
                          value={qty}
                          onChange={(e) => {
                            const val = Math.max(1, Math.min(youHave, Number(e.target.value) || 1));
                            setSellQuantity(prev => ({ ...prev, [id]: val }));
                          }}
                          style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '4px',
                            color: '#fff',
                            padding: '4px 8px',
                            width: '60px',
                            textAlign: 'center'
                          }}
                        />
                      </div>
                    </div>

                    <button
                      onClick={() => handleSell(id)}
                      disabled={youHave <= 0}
                      style={{
                        background: youHave > 0 ? 'rgba(40, 167, 69, 0.8)' : 'rgba(108, 117, 125, 0.5)',
                        border: 'none',
                        color: '#fff',
                        padding: '10px',
                        borderRadius: '6px',
                        cursor: youHave > 0 ? 'pointer' : 'not-allowed',
                        width: '100%',
                        fontWeight: 'bold',
                        opacity: youHave > 0 ? 1 : 0.6,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px'
                      }}
                    >
                      Sell for 
                      <img src="/items/coin.png" alt="coins" style={{ width: 16, height: 16 }} />
                      {(price * qty).toLocaleString()}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TMTrainerModal;
